'use client';

import { useEffect } from 'react';

const CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * Herlaadt langdraaiende tabs automatisch na een deploy, zodat niemand een hard refresh
 * hoeft te doen. Polt /api/health (levert de Next BUILD_ID van de draaiende container);
 * wijkt die af van de build waarmee deze tab geladen is, dan herladen we — maar nooit
 * midden in actief gebruik:
 *  - is de tab verborgen, dan herladen we direct (onzichtbaar voor de gebruiker);
 *  - is de tab zichtbaar, dan herladen we op de eerstvolgende visibility-wissel
 *    (gebruiker verlaat de tab of keert ernaar terug).
 * Nieuwe paginaladingen krijgen sowieso de nieuwe build; dit dekt alleen open tabs en
 * voorkomt en passant ChunkLoadErrors op oude bundles na een blue-green swap.
 */
export function BuildVersionAutoReload() {
  useEffect(() => {
    let initialVersion: string | null = null;
    let stale = false;
    let disposed = false;

    const check = async () => {
      try {
        const res = await fetch('/api/health', { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string | null };
        const version = typeof data.version === 'string' && data.version ? data.version : null;
        if (!version || disposed) return;
        if (initialVersion === null) {
          initialVersion = version;
          return;
        }
        if (version !== initialVersion && !stale) {
          stale = true;
          if (document.hidden) window.location.reload();
        }
      } catch {
        // offline of deploy-swap onderweg — volgende poll probeert opnieuw
      }
    };

    const onVisibilityChange = () => {
      if (stale) window.location.reload();
    };

    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);
    void check();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return null;
}
